import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import useAccess from "../../hooks/useAccess";
import Pagination from "../../components/Pagination";
import StyledSelect from "../../components/StyledSelect";
import "../../styles/ApiTokensPage.css";

const ITEMS_PER_PAGE = 10;

const ACCESS_TAGS = [
  { value: "drivers_info", label: "Drivers Info" },
  { value: "incident_violation", label: "Incident and Violation" },
  { value: "incident", label: "Incident" },
  { value: "violation", label: "Violation" },
];
const ACCESS_TAG_RESOURCES = {
  entire_organization: ["incidents", "violations"],
  incident_violation: ["incidents", "violations"],
  incident: ["incidents"],
  violation: ["violations"],
  drivers_info: ["drivers_phone"],
};
const DEFAULT_SCOPES_BY_TAG = {
  drivers_info: ["drivers_phone:read"],
  entire_organization: [
    "incidents:read",
    "incidents:write",
    "violations:read",
    "violations:write",
  ],
  incident_violation: ["incidents:read", "violations:read"],
  incident: ["incidents:read"],
  violation: ["violations:read"],
};
const SCOPE_GROUPS = [
  { resource: "drivers_phone", label: "Phone number", permissions: [
    { value: "drivers_phone:read", mode: "Read", description: "Read driver mobile and home phone numbers" },
    { value: "drivers_phone:write", mode: "Write", description: "Update driver mobile and home phone numbers" },
  ] },
  {
    resource: "incidents",
    label: "Incidents",
    permissions: [
      { value: "incidents:read", mode: "Read", description: "List incidents and view incident details" },
      { value: "incidents:write", mode: "Write", description: "Create and update incident records" },
    ],
  },
  {
    resource: "violations",
    label: "Violations",
    permissions: [
      { value: "violations:read", mode: "Read", description: "List violations and view violation details" },
      { value: "violations:write", mode: "Write", description: "Create and update violation records" },
    ],
  },
];
const EMPTY_FORM = {
  name: "",
  access_tag: "drivers_info",
  scopes: [...DEFAULT_SCOPES_BY_TAG.drivers_info],
  expires_at: "",
};

const labelForTag = (value) =>
  ACCESS_TAGS.find((tag) => tag.value === value)?.label || value;

const toLocalDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const displayDate = (value) =>
  value ? new Date(value).toLocaleString() : "Never";

const scopesForToken = (token) => {
  let scopes = token?.scopes;
  try {
    scopes = Array.isArray(scopes) ? scopes : JSON.parse(scopes || "[]");
  } catch {
    scopes = [];
  }
  if (scopes.length) return scopes;
  return (ACCESS_TAG_RESOURCES[token?.access_tag] || []).flatMap((resource) => [
    ...(token?.can_read ? [`${resource}:read`] : []),
    ...(token?.can_write ? [`${resource}:write`] : []),
  ]);
};

const scopeCounts = (tokenOrScopes) => {
  const scopes = Array.isArray(tokenOrScopes)
    ? tokenOrScopes
    : scopesForToken(tokenOrScopes);
  return {
    read: scopes.filter((scope) => scope.endsWith(":read")).length,
    write: scopes.filter((scope) => scope.endsWith(":write")).length,
  };
};

const ApiTokensPage = () => {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
  const { hasPermission } = useAccess();
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` }),
    [],
  );
  const [tokens, setTokens] = useState([]);
  const [tags, setTags] = useState(ACCESS_TAGS);
  const [tagDraft, setTagDraft] = useState({ label: "", scopes: ["drivers_phone:read"] });
  const [addingTag, setAddingTag] = useState(false);
  const [tagError, setTagError] = useState("");
  const [savingTag, setSavingTag] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [openMenu, setOpenMenu] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [newSecret, setNewSecret] = useState("");
  const [tokenCopied, setTokenCopied] = useState(false);
  const [copyError, setCopyError] = useState("");
  useEffect(() => { setTokenCopied(false); setCopyError(""); }, [newSecret]);
  const copyNewToken = async () => {
    try { await navigator.clipboard.writeText(newSecret); setTokenCopied(true); setCopyError(""); }
    catch { setTokenCopied(false); setCopyError("Could not copy. Please select and copy the token manually."); }
  };
  const [currentPage, setCurrentPage] = useState(1);
  const [scopePickerOpen, setScopePickerOpen] = useState(false);
  const [scopePickerPosition, setScopePickerPosition] = useState(null);
  const menuRef = useRef(null);
  const scopePickerRef = useRef(null);
  const canEdit = hasPermission("AccountSetting", "API Tokens", "edit");
  const canDelete = hasPermission("AccountSetting", "API Tokens", "delete");
  const pageTokens = useMemo(
    () =>
      tokens.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE,
      ),
    [currentPage, tokens],
  );
  const allowedScopeGroups = SCOPE_GROUPS;
  const allowedScopes = allowedScopeGroups.flatMap((group) =>
    group.permissions.map((permission) => permission.value),
  );
  const selectedScopeCounts = scopeCounts(form.scopes);
  const pickerScopes = addingTag ? tagDraft.scopes : form.scopes;
  const tagScopeCounts = scopeCounts(tagDraft.scopes);
  const allScopesSelected =
    allowedScopes.length > 0 &&
    allowedScopes.every((scope) => pickerScopes.includes(scope));

  const changeAccessTag = (accessTag) => {
    if (accessTag === "__add_tag__") {
      setScopePickerOpen(false);
      setTagError("");
      setAddingTag(true);
      return;
    }
    setForm({
      ...form,
      access_tag: accessTag,
      scopes: [...(tags.find((tag) => tag.value === accessTag)?.scopes || DEFAULT_SCOPES_BY_TAG[accessTag] || [])],
    });
  };

  const saveCustomTag = async () => {
    if (!tagDraft.label.trim() || !tagDraft.scopes.length || savingTag) return;
    setSavingTag(true);
    try {
      const { data } = await axios.post(`${BASE_URL}/api/api-tokens/tags`, tagDraft, { headers });
      setTags((current) => [...current, data]);
      setForm((current) => ({ ...current, access_tag: data.value, scopes: [...data.scopes] }));
      setAddingTag(false);
      setTagDraft({ label: "", scopes: ["drivers_phone:read"] });
      setMessage("");
    } catch (error) { setTagError(error.response?.data?.message || "Could not create tag."); }
    finally { setSavingTag(false); }
  };

  const toggleScope = (scope) => {
    const update = addingTag ? setTagDraft : setForm;
    update((current) => ({
      ...current,
      scopes: current.scopes.includes(scope)
        ? current.scopes.filter((item) => item !== scope)
        : [...current.scopes, scope],
    }));
  };

  const toggleAllScopes = () => {
    const update = addingTag ? setTagDraft : setForm;
    update((current) => ({
      ...current,
      scopes: allScopesSelected ? [] : [...allowedScopes],
    }));
  };

  const toggleScopePicker = (event) => {
    if (scopePickerOpen) {
      setScopePickerOpen(false);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const availableBelow = window.innerHeight - rect.bottom - 12;
    const panelHeight = Math.min(360, Math.max(240, availableBelow));
    const openAbove = availableBelow < 260 && rect.top > availableBelow;
    setScopePickerPosition({
      left: rect.left,
      width: rect.width,
      maxHeight: openAbove ? Math.min(360, rect.top - 12) : panelHeight,
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
    setScopePickerOpen(true);
  };

  const loadTokens = useCallback(async () => {
    try {
      setLoading(true);
      const [{ data }, { data: tagData }] = await Promise.all([
        axios.get(`${BASE_URL}/api/api-tokens`, { headers }),
        axios.get(`${BASE_URL}/api/api-tokens/tags`, { headers }),
      ]);
      setTags(Array.isArray(tagData) ? tagData : ACCESS_TAGS);
      setTokens(Array.isArray(data) ? data : []);
      setMessage("");
    } catch (error) {
      setMessage(error.response?.data?.message || "Unable to load API tokens.");
    } finally {
      setLoading(false);
    }
  }, [BASE_URL, headers]);

  useEffect(() => { loadTokens(); }, [loadTokens]);
  useEffect(() => { setScopePickerOpen(false); }, [addingTag]);
  useEffect(() => {
    const close = (event) => {
      if (
        !menuRef.current?.contains(event.target) &&
        !event.target?.closest?.(".api-token-menu-button")
      ) {
        setOpenMenu(null);
      }
      if (!scopePickerRef.current?.contains(event.target)) {
        setScopePickerOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, []);

  const toggleMenu = (event, token) => {
    if (openMenu?.token.id === token.id) {
      setOpenMenu(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 156;
    const menuHeight = 126;
    const left = Math.min(
      window.innerWidth - menuWidth - 10,
      Math.max(10, rect.right - menuWidth),
    );
    const top =
      rect.bottom + menuHeight + 8 <= window.innerHeight
        ? rect.bottom + 6
        : Math.max(10, rect.top - menuHeight - 6);

    setOpenMenu({ token, top, left });
  };

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, scopes: [...EMPTY_FORM.scopes] });
    setDialog({ type: "create" });
    setScopePickerOpen(false);
    setMessage("");
  };

  const openEdit = (token) => {
    setForm({
      name: token.name,
      access_tag: token.access_tag === "entire_organization" ? "incident_violation" : token.access_tag,
      scopes: scopesForToken(token),
      expires_at: toLocalDateTime(token.expires_at),
    });
    setDialog({ type: "edit", token });
    setOpenMenu(null);
    setScopePickerOpen(false);
    setMessage("");
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.scopes.length) {
      setMessage("Select at least one API permission.");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        ...form,
        expires_at: form.expires_at
          ? new Date(form.expires_at).toISOString()
          : null,
      };
      if (dialog.type === "create") {
        const { data } = await axios.post(`${BASE_URL}/api/api-tokens`, payload, { headers });
        setNewSecret(data.token);
      } else {
        await axios.put(`${BASE_URL}/api/api-tokens/${dialog.token.id}`, payload, { headers });
      }
      setDialog(null);
      setMessage("");
      await loadTokens();
    } catch (error) {
      setMessage(error.response?.data?.message || "Unable to save the API token.");
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async (token) => {
    setOpenMenu(null);
    if (!window.confirm(`Regenerate "${token.name}"? The current token will stop working immediately.`)) return;
    try {
      setSaving(true);
      const { data } = await axios.post(`${BASE_URL}/api/api-tokens/${token.id}/regenerate`, {}, { headers });
      setNewSecret(data.token);
      setMessage("");
      await loadTokens();
    } catch (error) {
      setMessage(error.response?.data?.message || "Unable to regenerate the API token.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (token) => {
    setOpenMenu(null);
    if (!window.confirm(`Delete "${token.name}"? Any integration using it will immediately lose access.`)) return;
    try {
      setSaving(true);
      await axios.delete(`${BASE_URL}/api/api-tokens/${token.id}`, { headers });
      setMessage("");
      await loadTokens();
    } catch (error) {
      setMessage(error.response?.data?.message || "Unable to delete the API token.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="account-settings-tab-panel api-tokens-page">
      <div className="account-settings-tab-header">
        <div>
          <h2>API Tokens</h2>
          <p>Create tokens for approved integrations and limit each one by data area and read/write access.</p>
        </div>
        {canEdit && (
          <button className="btn btn-primary api-token-add" type="button" onClick={openCreate}>
            <i className="fas fa-plus" aria-hidden="true" /> Add an API Token
          </button>
        )}
      </div>

      {message && <div className="alert alert-danger py-2" role="alert">{message}</div>}

      <div className="api-token-help">
        <i className="fas fa-info-circle" aria-hidden="true" />
        <span>The complete token is shown only once. Store it securely; regenerate it if it is lost or expired.</span>
      </div>

      <div className="api-token-table-shell nw-data-table-shell">
        <div className="table-responsive nw-data-table-responsive api-token-table-scroll">
        <table className="table align-middle mb-0 nw-data-table nw-data-table-dense api-token-table" data-no-enhance-table="true">
          <thead><tr><th><span className="nw-static-header">Name</span></th><th><span className="nw-static-header">Access Token</span></th><th><span className="nw-static-header">Scope</span></th><th><span className="nw-static-header">Expires</span></th><th><span className="nw-static-header">Tag Access</span></th><th className="api-token-action-heading"><span className="nw-static-header">Actions</span></th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="api-token-empty">Loading API tokens...</td></tr>
            ) : tokens.length === 0 ? (
              <tr><td colSpan="6" className="api-token-empty">No API tokens have been created.</td></tr>
            ) : pageTokens.map((token) => {
              const expired = token.expires_at && new Date(token.expires_at).getTime() <= Date.now();
              const counts = scopeCounts(token);
              return (
                <tr key={token.id}>
                  <td><strong>{token.name}</strong><small>Created {displayDate(token.created_at)}</small></td>
                  <td><code>{token.token_prefix}</code><small>Last used: {displayDate(token.last_used_at)}</small></td>
                  <td><div className="api-token-scope-counts"><div>Read: <b>{counts.read}</b></div><div>Write: <b>{counts.write}</b></div></div></td>
                  <td><span className={expired ? "api-token-expired" : ""}>{expired ? "Expired" : displayDate(token.expires_at)}</span></td>
                  <td>{tags.find((tag) => tag.value === token.access_tag)?.label || labelForTag(token.access_tag)}</td>
                  <td className="api-token-actions">
                    {(canEdit || canDelete) && <>
                      <button type="button" data-no-standard-button="true" aria-haspopup="menu" aria-expanded={openMenu?.token.id === token.id} aria-label={`Actions for ${token.name}`} className="app-table-action-button api-token-menu-button" onClick={(event) => toggleMenu(event, token)}><i className="fas fa-ellipsis-h" aria-hidden="true" /></button>
                    </>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        <Pagination currentPage={currentPage} totalItems={tokens.length} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setCurrentPage} scrollToTopOnChange={false} />
      </div>

      {openMenu && createPortal(
        <div ref={menuRef} className="api-token-menu" role="menu" style={{ top: openMenu.top, left: openMenu.left }}>
          {canEdit && <button type="button" data-no-standard-button="true" role="menuitem" onClick={() => openEdit(openMenu.token)}><i className="fas fa-pen" aria-hidden="true" /> Edit</button>}
          {canEdit && <button type="button" data-no-standard-button="true" role="menuitem" onClick={() => regenerate(openMenu.token)}><i className="fas fa-sync-alt" aria-hidden="true" /> Regenerate</button>}
          {canDelete && <button type="button" data-no-standard-button="true" role="menuitem" className="danger" onClick={() => remove(openMenu.token)}><i className="fas fa-trash" aria-hidden="true" /> Delete</button>}
        </div>,
        document.body,
      )}

      {dialog && scopePickerOpen && scopePickerPosition && createPortal(
        <div ref={scopePickerRef} className="api-token-scope-panel is-portal" style={{ ...scopePickerPosition, ...(addingTag ? { zIndex: 14000 } : {}) }}>
          <label className="api-token-select-all"><input type="checkbox" checked={allScopesSelected} onChange={toggleAllScopes} /> <span>Select All Available Permissions</span></label>
          {allowedScopeGroups.map((group) => <section key={group.resource} className="api-token-scope-group"><h4>{group.label}</h4>{group.permissions.map((permission) => <label key={permission.value} className="api-token-scope-option"><input type="checkbox" checked={pickerScopes.includes(permission.value)} onChange={() => toggleScope(permission.value)} /><span><b>{permission.mode} {group.label}</b><small>{permission.description}</small></span></label>)}</section>)}
        </div>,
        document.body,
      )}

      <section className="api-token-documentation">
        <h3>Developer Documentation and Guides</h3>
        <ul><li><Link to="/api-documentation" target="_blank" rel="noopener noreferrer">REST API Documentation <i className="fas fa-external-link-alt" aria-hidden="true" /></Link> — learn about authentication, scopes, pagination, errors, endpoints, and request examples.</li></ul>
      </section>

      {dialog && <div className="api-token-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setDialog(null); }}>
        <div className="api-token-modal" role="dialog" aria-modal="true" aria-labelledby="api-token-dialog-title">
          <div className="api-token-modal-header"><h3 id="api-token-dialog-title">{dialog.type === "create" ? "Add API Token" : "Edit API Token"}</h3><button type="button" data-no-standard-button="true" className="app-window-close" aria-label="Close API token dialog" title="Close" onClick={() => setDialog(null)}><span aria-hidden="true">&times;</span></button></div>
          <form onSubmit={submit}>
            {message && <div className="alert alert-danger py-2 mb-0" role="alert">{message}</div>}
            <label><span className="api-token-field-label">Name <b>*</b></span><input className="form-control" value={form.name} maxLength="120" required onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Company or integration name" /></label>
            <div className="api-token-form-field"><span className="api-token-field-label">Tag Access <b>*</b></span><StyledSelect value={form.access_tag} options={canEdit ? [...tags, { value: "__add_tag__", className: "api-token-add-tag-row", label: <span className="api-token-add-tag-option"><i className="fas fa-plus" aria-hidden="true" /> Add New Tag Access</span> }] : tags} onChange={changeAccessTag} minWidth={0} ariaLabel="Select API token tag access" /></div>
            {selectedScopeCounts.read > 0 && <div className="api-token-pii-warning" role="note"><i className="fas fa-exclamation-triangle" aria-hidden="true" /><div>Selected read permissions may provide access to personally identifiable information (PII). Share this token only with approved integrations.</div></div>}
            <div className="api-token-form-field"><span className="api-token-field-label">Scope <b>*</b></span><div className="api-token-scope-picker"><button type="button" data-no-standard-button="true" className={`api-token-scope-toggle ${scopePickerOpen ? "open" : ""}`} aria-haspopup="true" aria-expanded={scopePickerOpen} onClick={toggleScopePicker}><span>{selectedScopeCounts.read} Read, {selectedScopeCounts.write} Write Selected</span><i className="fas fa-chevron-down" aria-hidden="true" /></button></div></div>
            <label>Expires (optional)<input className="form-control" type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} /><small>Leave blank for no expiration.</small></label>
            <div className="api-token-modal-footer"><button type="button" className="btn btn-light" onClick={() => setDialog(null)}>Cancel</button><button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving..." : dialog.type === "create" ? "Create Token" : "Save Changes"}</button></div>
          </form>
        </div>
      </div>}

      {addingTag && createPortal(<div className="api-token-modal-backdrop api-token-tag-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !savingTag) setAddingTag(false); }}>
        <div className="api-token-modal api-token-tag-modal" role="dialog" aria-modal="true" aria-labelledby="api-tag-title" onKeyDown={(event) => { if (event.key === "Escape" && !savingTag) { event.stopPropagation(); setAddingTag(false); } }}>
          <div className="api-token-modal-header"><h3 id="api-tag-title">Add New Tag Access</h3><button type="button" data-no-standard-button="true" className="app-window-close" aria-label="Close new tag dialog" disabled={savingTag} onClick={() => setAddingTag(false)}><span aria-hidden="true">&times;</span></button></div>
          <form onSubmit={(event) => { event.preventDefault(); saveCustomTag(); }}>
            {tagError && <div className="alert alert-danger" role="alert">{tagError}</div>}
            <label>Tag name<input autoFocus required className="form-control" maxLength={120} value={tagDraft.label} onChange={(event) => setTagDraft({ ...tagDraft, label: event.target.value })} placeholder="Driver phone number" /></label>
            <div className="api-token-form-field"><span className="api-token-field-label">Scope <b>*</b></span><div className="api-token-scope-picker"><button type="button" data-no-standard-button="true" className={`api-token-scope-toggle ${scopePickerOpen ? "open" : ""}`} aria-haspopup="true" aria-expanded={scopePickerOpen} onClick={toggleScopePicker}><span>{tagScopeCounts.read} Read, {tagScopeCounts.write} Write Selected</span><i className="fas fa-chevron-down" aria-hidden="true" /></button></div><small>Select the permissions checked by default when this tag is chosen.</small></div>
            <div className="api-token-modal-footer"><button type="button" className="btn btn-light" disabled={savingTag} onClick={() => setAddingTag(false)}>Cancel</button><button type="submit" className="btn btn-primary" disabled={savingTag || !tagDraft.label.trim() || !tagDraft.scopes.length}>{savingTag ? "Saving..." : "Save Tag"}</button></div>
          </form>
        </div>
      </div>, document.body)}

      {newSecret && <div className="api-token-modal-backdrop"><div className="api-token-modal api-token-secret" role="dialog" aria-modal="true" aria-labelledby="new-token-title"><div className="api-token-modal-header"><h3 id="new-token-title">Copy Your API Token</h3></div><p>This is the only time the complete token will be displayed.</p><div className="api-token-secret-value"><code>{newSecret}</code><button type="button" className="btn btn-outline-primary" onClick={copyNewToken}>{tokenCopied ? "Copied" : "Copy"}</button></div><span role="status" className="visually-hidden">{tokenCopied ? "Token copied to clipboard" : ""}</span>{copyError && <p role="alert">{copyError}</p>}<div className="api-token-modal-footer"><button type="button" className="btn btn-primary" onClick={() => setNewSecret("")}>I Have Saved It</button></div></div></div>}
    </div>
  );
};

export default ApiTokensPage;
